import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { AffiliateAuthProvider } from "@/lib/affiliateAuth";
import { CaptureProvider } from "@/lib/capture";
import { AssessmentProvider } from "@/lib/assessment";
import { ApplicationTrayProvider } from "@/lib/applicationTray";
import ToastHost from "@/components/Toast";
import ScrollToTop from "@/components/ScrollToTop";
import AppShell from "@/components/AppShell";
import AffiliateShell from "@/components/AffiliateShell";
import useAffiliateTracking from "@/lib/useAffiliateTracking";

// Public pages
import Landing from "@/pages/Landing";
import About from "@/pages/About";
import MarketingProgram from "@/pages/MarketingProgram";
import SignIn from "@/pages/SignIn";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import GetAccess from "@/pages/GetAccess";
import PhoneOffer from "@/pages/PhoneOffer";
import Welcome from "@/pages/Welcome";
import LegalPage from "@/pages/LegalPage";
import WhatWeHelpWith, { WhatWeHelpWithInApp } from "@/pages/WhatWeHelpWith";
// Research is now the /catalogue page — module still lives at
// `pages/Research.jsx` for continuity, exported as the Catalogue
// component (see file header). /research and / both redirect to it.
import Catalogue from "@/pages/Research";

// Affiliate (public + auth)
import MarketingAffiliates from "@/pages/MarketingAffiliates";
import AffiliateApply from "@/pages/affiliate/AffiliateApply";
import AffiliateSignIn from "@/pages/affiliate/AffiliateSignIn";
import AffiliateSetup from "@/pages/affiliate/AffiliateSetup";
import AffiliateOverview from "@/pages/affiliate/dashboard/Overview";
import AffiliateConversions from "@/pages/affiliate/dashboard/Conversions";
import AffiliatePayouts from "@/pages/affiliate/dashboard/Payouts";
import AffiliateAssets from "@/pages/affiliate/dashboard/Assets";
import AssetsIndex from "@/pages/affiliate/dashboard/assets/AssetsIndex";
import AssetsCompliance from "@/pages/affiliate/dashboard/assets/AssetsCompliance";
import AssetsPlaybooks from "@/pages/affiliate/dashboard/assets/AssetsPlaybooks";
import AssetsPlaybookReader from "@/pages/affiliate/dashboard/assets/AssetsPlaybookReader";
import AssetsHooks from "@/pages/affiliate/dashboard/assets/AssetsHooks";
import AssetsPosts from "@/pages/affiliate/dashboard/assets/AssetsPosts";
import AssetsBenefitCards from "@/pages/affiliate/dashboard/assets/AssetsBenefitCards";
import AssetsStoryIdeas from "@/pages/affiliate/dashboard/assets/AssetsStoryIdeas";
import AssetsComparison from "@/pages/affiliate/dashboard/assets/AssetsComparison";
import AssetsFAQs from "@/pages/affiliate/dashboard/assets/AssetsFAQs";
import AssetsDisclaimers from "@/pages/affiliate/dashboard/assets/AssetsDisclaimers";
import AssetsMarketing from "@/pages/affiliate/dashboard/assets/AssetsMarketing";
import AffiliateProfile from "@/pages/affiliate/dashboard/Profile";

// App pages
import MemberDashboard from "@/pages/app/MemberDashboard";
import CareTeamChat from "@/pages/app/CareTeamChat";
import AppHome from "@/pages/app/AppHome";
import Programs from "@/pages/app/Programs";
import Browse from "@/pages/app/Browse";
import ComingSoonProgram from "@/pages/app/ComingSoonProgram";
import Reattestation from "@/pages/app/Reattestation";
import ProgramDetail from "@/pages/app/ProgramDetail";
import AssessmentFlow, { AssessmentSubmitted } from "@/pages/app/AssessmentFlow";
import DiscoveryRecommendation from "@/pages/app/DiscoveryRecommendation";
import PostPaymentWelcome from "@/pages/app/PostPaymentWelcome";
import Checkout from "@/pages/app/Checkout";
import ResearchCheckout from "@/pages/app/ResearchCheckout";
import ResearchDone from "@/pages/app/ResearchDone";
import ProtocolDetail from "@/pages/app/ProtocolDetail";
import Assessment from "@/pages/app/Assessment";
import Done from "@/pages/app/Done";
import Plan from "@/pages/app/Plan";
import CheckIn from "@/pages/app/CheckIn";
import Messages from "@/pages/app/Messages";
import Orders from "@/pages/app/Orders";
import Refer from "@/pages/app/Refer";
import Me from "@/pages/app/Me";
import InjectionOnboarding from "@/pages/app/injection/InjectionOnboarding";
import InjectionReference from "@/pages/app/injection/InjectionReference";
import InjectionLog from "@/pages/app/injection/InjectionLog";
import InjectionHistory from "@/pages/app/injection/InjectionHistory";

function AppRoutes() {
  useAffiliateTracking();
  return (
    <Routes>
      {/* Public */}
      {/* Root redirects to the research catalogue — that's the landing
          default the doctor-share pattern relies on (voya9.com opens
          the catalogue directly). Employer-facing marketing lives at
          /for-employers. */}
      <Route path="/" element={<Navigate to="/catalogue" replace />} />
      <Route path="/catalogue" element={<Catalogue />} />
      {/* Backward-compat alias — /research redirects to /catalogue so
          any doctor-shared old links keep working. */}
      <Route path="/research" element={<Navigate to="/catalogue" replace />} />
      {/* Employer-sponsored VOYA9 (re-adapted original landing) */}
      <Route path="/for-employers" element={<Landing />} />
      <Route path="/about" element={<About />} />
      <Route path="/programs/:slug" element={<MarketingProgram />} />
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/get-access" element={<GetAccess />} />
      <Route path="/phone-offer" element={<PhoneOffer />} />
      <Route path="/welcome" element={<Welcome />} />
      <Route path="/legal/terms" element={<LegalPage kind="terms" />} />
      <Route path="/legal/privacy" element={<LegalPage kind="privacy" />} />
      <Route path="/legal/telehealth" element={<LegalPage kind="telehealth" />} />
      <Route path="/what-we-help-with" element={<WhatWeHelpWith />} />

      {/* Affiliate public */}
      <Route path="/affiliates" element={<MarketingAffiliates />} />
      <Route path="/affiliates/apply" element={<AffiliateApply />} />
      <Route path="/affiliates/sign-in" element={<AffiliateSignIn />} />
      <Route path="/affiliates/setup" element={<AffiliateSetup />} />

      {/* Affiliate gated dashboard */}
      <Route path="/affiliates/dashboard" element={<AffiliateShell />}>
        <Route index element={<AffiliateOverview />} />
        <Route path="conversions" element={<AffiliateConversions />} />
        <Route path="payouts" element={<AffiliatePayouts />} />
        <Route path="assets" element={<AffiliateAssets />}>
          <Route index element={<AssetsIndex />} />
          <Route path="compliance" element={<AssetsCompliance />} />
          <Route path="playbooks" element={<AssetsPlaybooks />} />
          <Route path="playbooks/:slug" element={<AssetsPlaybookReader />} />
          <Route path="hooks" element={<AssetsHooks />} />
          <Route path="posts" element={<AssetsPosts />} />
          <Route path="benefit-cards" element={<AssetsBenefitCards />} />
          <Route path="story-ideas" element={<AssetsStoryIdeas />} />
          <Route path="comparison" element={<AssetsComparison />} />
          <Route path="faqs" element={<AssetsFAQs />} />
          <Route path="disclaimers" element={<AssetsDisclaimers />} />
          <Route path="marketing" element={<AssetsMarketing />} />
        </Route>
        <Route path="profile" element={<AffiliateProfile />} />
      </Route>

      {/* Authenticated consumer */}
      <Route path="/app" element={<AppShell />}>
        <Route index element={<MemberDashboard />} />
        <Route path="home" element={<AppHome />} />
        <Route path="programs" element={<Programs />} />
        <Route path="programs/:slug/coming-soon" element={<ComingSoonProgram />} />
        <Route path="programs/:slug" element={<ProgramDetail />} />
        <Route path="browse" element={<Browse />} />
        <Route path="what-we-help-with" element={<WhatWeHelpWithInApp />} />
        <Route path="reattestation" element={<Reattestation />} />
        <Route path="assessment/:sessionId/submitted" element={<AssessmentSubmitted />} />
        <Route path="assessment/:sessionId/recommendation" element={<DiscoveryRecommendation />} />
        <Route path="assessment/:sessionId/welcome" element={<PostPaymentWelcome />} />
        <Route path="checkout/:sessionId" element={<Checkout />} />
        <Route path="research/checkout" element={<ResearchCheckout />} />
        <Route path="research/done" element={<ResearchDone />} />
        <Route path="assessment/:sessionId" element={<AssessmentFlow />} />
        <Route path="assessment-legacy/:slug" element={<Assessment />} />
        <Route path="done" element={<Done />} />
        <Route path="plan" element={<Plan />} />
        <Route path="plan/checkin" element={<CheckIn />} />
        <Route path="plan/messages" element={<Messages />} />
        <Route path="plan/orders" element={<Orders />} />
        <Route path="care-team/:consultationId" element={<CareTeamChat />} />
        <Route path="care-team" element={<CareTeamChat />} />
        <Route path="refer" element={<Refer />} />
        <Route path="me" element={<Me />} />
        <Route path="onboarding/injection" element={<InjectionOnboarding />} />
        <Route path="reference/injections" element={<InjectionReference />} />
        <Route path="injection/log" element={<InjectionLog />} />
        <Route path="injection/history" element={<InjectionHistory />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <AffiliateAuthProvider>
            <CaptureProvider>
              <AssessmentProvider>
                <ApplicationTrayProvider>
                  <AppRoutes />
                  <ToastHost />
                </ApplicationTrayProvider>
              </AssessmentProvider>
            </CaptureProvider>
          </AffiliateAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
